// ─────────────────────────────────────────────────────────────────────────────
// AUTH CONTEXT — talks to local PostgreSQL API (no Supabase)
// ─────────────────────────────────────────────────────────────────────────────
import { createContext, useContext, useEffect, useState, useCallback } from "react";

const AuthContext = createContext(null);
const API = "/api";

let accessToken = null; // in-memory mirror of localStorage, kept for refresh()
function getAccessToken() { return accessToken ?? localStorage.getItem("axis_token"); }
function setAccessToken(t) {
  accessToken = t;
  if (t) localStorage.setItem("axis_token", t);
  else localStorage.removeItem("axis_token");
}

// The refresh token itself lives only in an httpOnly cookie the browser
// manages automatically — this module never sees its value. Concurrent 401s
// must all await the *same* refresh call rather than each firing their own,
// since the refresh token is single-use and the first response rotates it
// out from under any other in-flight refresh request.
let refreshInFlight = null;
async function refreshAccessToken() {
  if (!refreshInFlight) {
    refreshInFlight = fetch(API + "/auth/refresh", {
      method: "POST",
      credentials: "include",
    })
      .then(async (res) => {
        if (!res.ok) { setAccessToken(null); return null; }
        const data = await res.json();
        setAccessToken(data.token);
        return data.token;
      })
      .catch(() => { setAccessToken(null); return null; })
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

async function apiFetch(path, options = {}, _retried = false) {
  const token = getAccessToken();
  const res = await fetch(API + path, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });

  // A 401 on anything other than the auth endpoints themselves might just
  // mean the 15-minute access token expired mid-session — try one silent
  // refresh-and-retry before treating it as a real auth failure.
  const isAuthRoute = path.startsWith("/auth/login") || path.startsWith("/auth/signup") || path.startsWith("/auth/refresh");
  if (res.status === 401 && !_retried && !isAuthRoute) {
    const newToken = await refreshAccessToken();
    if (newToken) return apiFetch(path, options, true);
  }

  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error || "Request failed");
    if (data.needsVerification) { err.needsVerification = true; err.email = data.email; }
    throw err;
  }
  return data;
}

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [profile, setProfile] = useState(null);
  const [org,     setOrg]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      let token = getAccessToken();
      if (!token) {
        // No access token (fresh tab, or one that's aged past 15 minutes) —
        // a valid refresh cookie from up to 7 days ago may still be live,
        // so try a silent refresh before concluding the user is logged out.
        token = await refreshAccessToken();
      }
      if (!token) { setLoading(false); return; }
      try {
        const { profile, org } = await apiFetch("/auth/me");
        setProfile(profile);
        setOrg(org);
        setUser({ id:profile.id, email:profile.email, role:profile.role, orgId:profile.org_id });
      } catch {
        setAccessToken(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Proactively rotate the access token every 10 minutes (comfortably inside
  // its 15-minute lifetime) instead of only reacting to a 401. This matters
  // beyond just this context's own api() calls: a large fraction of modules
  // in this app read localStorage.getItem("axis_token") directly for their
  // own fetch calls rather than going through here, and none of them have
  // any retry-on-401 logic of their own. Keeping the stored token fresh in
  // the background means every one of those call sites keeps working too,
  // without having to touch each of them individually.
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => { refreshAccessToken(); }, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  async function signIn(email, password) {
    const data = await apiFetch("/auth/login", {
      method:"POST", body:JSON.stringify({ email, password }),
    });
    setAccessToken(data.token);
    setProfile(data.profile); setOrg(data.org);
    setUser({ id:data.profile.id, email:data.profile.email, role:data.profile.role, orgId:data.profile.org_id });
    return data;
  }

  async function signUp({ email, password, fullName, orgName, level='company' }) {
    const data = await apiFetch("/auth/signup", {
      method:"POST", body:JSON.stringify({ email, password, fullName, orgName, level }),
    });
    if (data.emailSent) {
      // Email verification required — don't auto-login
      return data;
    }
    setAccessToken(data.token);
    setProfile(data.profile); setOrg(data.org);
    setUser({ id:data.profile.id, email:data.profile.email, role:data.profile.role, orgId:data.profile.org_id });
    return data;
  }

  async function signOut() {
    // Best-effort — revoke the refresh token server-side so a copied/leaked
    // one can't outlive an explicit logout. Local state clears regardless of
    // whether the network call succeeds.
    try { await apiFetch("/auth/logout", { method: "POST" }); } catch {}
    setAccessToken(null);
    setUser(null); setProfile(null); setOrg(null);
  }

  async function signOutAllDevices() {
    try { await apiFetch("/auth/logout-all", { method: "POST" }); } catch {}
    setAccessToken(null);
    setUser(null); setProfile(null); setOrg(null);
  }

  // Asks the server to email a reset link. The server answers the same way whether or not the address exists.
  async function resetPassword(email) {
    await apiFetch("/auth/forgot-password", {
      method:"POST", body:JSON.stringify({ email }),
    });
  }

  async function changePassword(currentPassword, newPassword) {
    await apiFetch("/auth/change-password", {
      method:"POST", body:JSON.stringify({ currentPassword, newPassword }),
    });
  }

  const refreshProfile = useCallback(async () => {
    if (!getAccessToken()) return;
    try {
      const { profile: p, org: o } = await apiFetch("/auth/me");
      setProfile(p); setOrg(o);
    } catch (err) { console.warn("Failed to refresh profile:", err.message); }
  }, []);

  const api      = useCallback((path, opts) => apiFetch(path, opts), []);
  const role     = user?.role || null;
  const isAdmin  = role === "org_admin" || role === "super_admin";

  return (
    <AuthContext.Provider value={{
      user, profile, org, role, loading, isAdmin,
      signIn, signUp, signOut, signOutAllDevices, changePassword, resetPassword, api, refreshProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
